import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { makeAuthenticatedRequest, handleApiError } from '../utils/api';
import { Character } from '../types/character';
import { CharacterFullStats } from '../types/skillTree';
import SkillTree from './SkillTree';
import './CharacterProgression.css';

interface CharacterProgressionProps {
  characterId: number;
  onBack?: () => void;
}

interface TownBonus {
  townName: string;
  townType: string;
  bonusType: string;
  bonusName: string;
  description: string;
  bonusValue: number;
  slot: number;
  tier: number;
}

const CharacterProgression: React.FC<CharacterProgressionProps> = ({ characterId, onBack }) => {
  const { token } = useAuth();
  const [character, setCharacter] = useState<Character | null>(null);
  const [fullStats, setFullStats] = useState<CharacterFullStats | null>(null);
  const [activeTab, setActiveTab] = useState<'skills' | 'stats'>('skills');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [townBonuses, setTownBonuses] = useState<TownBonus[]>([]);

  useEffect(() => {
    loadCharacterData();
    loadFullStats();
    loadTownBonuses();
  }, [characterId]);

  const loadCharacterData = async () => {
    try {
      const response = await makeAuthenticatedRequest(`/characters/${characterId}`);

      if (!response.ok) {
        let errorMessage = 'Failed to load character';
        const textResponse = await response.clone().text();
        try {
          const errorData = JSON.parse(textResponse);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          errorMessage = textResponse || `Server error: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setCharacter(data);
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
    }
  };

  const loadFullStats = async () => {
    try {
      setLoading(true);
      const response = await makeAuthenticatedRequest(`/skilltree/character/${characterId}/full-stats`);

      if (!response.ok) {
        let errorMessage = 'Failed to load character stats';
        const textResponse = await response.clone().text();
        try {
          const errorData = JSON.parse(textResponse);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          errorMessage = textResponse || `Server error: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setFullStats(data);
      setError(null);
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const loadTownBonuses = async () => {
    try {
      const response = await makeAuthenticatedRequest('/town/bonuses/active');

      if (!response.ok) {
        console.warn('Failed to load town bonuses');
        return;
      }

      const data = await response.json();
      setTownBonuses(data);
    } catch (err) {
      console.warn('Error loading town bonuses:', err);
    }
  };

  const renderStatsTab = () => {
    if (!character || !fullStats) return null;

    return (
      <div className="character-stats-container">
        <div className="character-info-card">
          <div className="character-header">
            <h2>{character.name}</h2>
            <div className="character-level">Level {character.level}</div>
          </div>
          <div className="character-class-race">
            <span className="character-class">{character.class}</span>
            <span className="character-race">{character.race}</span>
          </div>
        </div>

        <div className="stats-grid">
          <div className="stats-section">
            <h3>Base Stats</h3>
            <div className="stats-comparison">
              <div className="stat-row">
                <span className="stat-label">Vitality:</span>
                <span className="stat-base">{fullStats.baseStats.vitality}</span>
                {fullStats.passiveBonuses.vitality > 0 && (
                  <span className="stat-bonus">+{fullStats.passiveBonuses.vitality}</span>
                )}
                <span className="stat-final">= {fullStats.finalStats.vitality}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Intelligence:</span>
                <span className="stat-base">{fullStats.baseStats.intelligence}</span>
                {fullStats.passiveBonuses.intelligence > 0 && (
                  <span className="stat-bonus">+{fullStats.passiveBonuses.intelligence}</span>
                )}
                <span className="stat-final">= {fullStats.finalStats.intelligence}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Strength:</span>
                <span className="stat-base">{fullStats.baseStats.strength}</span>
                {fullStats.passiveBonuses.strength > 0 && (
                  <span className="stat-bonus">+{fullStats.passiveBonuses.strength}</span>
                )}
                <span className="stat-final">= {fullStats.finalStats.strength}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Dexterity:</span>
                <span className="stat-base">{fullStats.baseStats.dexterity}</span>
                {fullStats.passiveBonuses.dexterity > 0 && (
                  <span className="stat-bonus">+{fullStats.passiveBonuses.dexterity}</span>
                )}
                <span className="stat-final">= {fullStats.finalStats.dexterity}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Constitution:</span>
                <span className="stat-base">{fullStats.baseStats.constitution}</span>
                {fullStats.passiveBonuses.constitution > 0 && (
                  <span className="stat-bonus">+{fullStats.passiveBonuses.constitution}</span>
                )}
                <span className="stat-final">= {fullStats.finalStats.constitution}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Wisdom:</span>
                <span className="stat-base">{fullStats.baseStats.wisdom}</span>
                {fullStats.passiveBonuses.wisdom > 0 && (
                  <span className="stat-bonus">+{fullStats.passiveBonuses.wisdom}</span>
                )}
                <span className="stat-final">= {fullStats.finalStats.wisdom}</span>
              </div>
            </div>
          </div>

          <div className="stats-section">
            <h3>Derived Stats</h3>
            <div className="derived-stats">
              <div className="stat-row">
                <span className="stat-label">Health Points:</span>
                <span className="stat-final">{fullStats.derivedStats.currentHP} / {fullStats.derivedStats.maxHP}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Mana Points:</span>
                <span className="stat-final">{fullStats.derivedStats.currentMP} / {fullStats.derivedStats.maxMP}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Action Points:</span>
                <span className="stat-final">{fullStats.derivedStats.actionPoints}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Movement:</span>
                <span className="stat-final">{fullStats.derivedStats.movementPoints}</span>
              </div>
            </div>
          </div>

          <div className="stats-section">
            <h3>Combat Stats</h3>
            <div className="combat-stats">
              <div className="stat-row">
                <span className="stat-label">Critical Hit Chance:</span>
                <span className="stat-final">{fullStats.derivedStats.criticalHitChance}%</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Critical Hit Damage:</span>
                <span className="stat-final">{fullStats.derivedStats.criticalHitDamage}%</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Accuracy:</span>
                <span className="stat-final">{fullStats.derivedStats.accuracy}%</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Evasion:</span>
                <span className="stat-final">{fullStats.derivedStats.evasion}%</span>
              </div>
              {fullStats.combatBonuses.spellDamageBonus > 0 && (
                <div className="stat-row">
                  <span className="stat-label">Spell Damage Bonus:</span>
                  <span className="stat-final">+{fullStats.combatBonuses.spellDamageBonus}%</span>
                </div>
              )}
              {fullStats.combatBonuses.healingBonus > 0 && (
                <div className="stat-row">
                  <span className="stat-label">Healing Bonus:</span>
                  <span className="stat-final">+{fullStats.combatBonuses.healingBonus}%</span>
                </div>
              )}
            </div>
          </div>

          {fullStats.damageResistances.length > 0 && (
            <div className="stats-section">
              <h3>Damage Resistances</h3>
              <div className="resistance-stats">
                {fullStats.damageResistances.map(resistance => (
                  <div key={resistance.damageType} className="stat-row">
                    <span className="stat-label">{resistance.damageType}:</span>
                    <span className="stat-final">{resistance.resistancePercent}% resistance</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {fullStats.passiveAbilities.length > 0 && (
            <div className="stats-section">
              <h3>Active Passive Abilities</h3>
              <div className="passive-abilities">
                {fullStats.passiveAbilities.map(ability => (
                  <div key={ability} className="passive-ability-badge">
                    {ability}
                  </div>
                ))}
              </div>
            </div>
          )}

          {townBonuses.length > 0 && (
            <div className="stats-section">
              <h3>Town Bonuses</h3>
              <p className="town-bonuses-description">Bonuses from liberated towns apply globally to all your characters.</p>
              <div className="town-bonuses-list">
                {townBonuses.map((bonus, idx) => (
                  <div key={idx} className="town-bonus-card">
                    <div className="town-bonus-header">
                      <span className="town-name">{bonus.townName}</span>
                      <span className="town-type-badge">{bonus.townType}</span>
                    </div>
                    <div className="town-bonus-details">
                      <strong>{bonus.bonusName}</strong>
                      <span className="bonus-value">
                        {bonus.bonusType.includes('Percent') || bonus.bonusType.includes('Chance') || bonus.bonusType.includes('Multiplier')
                          ? `+${bonus.bonusValue}%`
                          : `+${bonus.bonusValue}`}
                      </span>
                    </div>
                    <p className="town-bonus-description">{bonus.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="loading-spinner">Loading character progression...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="character-progression-container">
      <div className="progression-header">
        {onBack && (
          <button className="back-to-characters-button" onClick={onBack}>
            ← Back to Characters
          </button>
        )}
        <div className="progression-tabs">
          <button
            className={`tab-button ${activeTab === 'skills' ? 'active' : ''}`}
            onClick={() => setActiveTab('skills')}
          >
            Skill Tree
          </button>
          <button
            className={`tab-button ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            Character Stats
          </button>
        </div>
      </div>

      <div className="tab-content">
        {activeTab === 'skills' && <SkillTree characterId={characterId} onNavigateToSkills={() => setActiveTab('skills')} />}
        {activeTab === 'stats' && renderStatsTab()}
      </div>
    </div>
  );
};

export default CharacterProgression;